import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './App.css';
import ChatInput from './components/ChatInput';
import Message from './components/Message';
import WelcomeScreen from './components/WelcomeScreen';
import QuestionParametersModal from './components/QuestionParametersModal';
import { getSession, logout } from './services/auth';
import { askChatbot, executeQuestion, getCategories, getQuestion, getQuestions } from './services/chatbotApi';

function ChatApp() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedCat, setExpandedCat] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadError, setLoadError] = useState('');
  const scrollRef = useRef(null);
  const profileRef = useRef(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const session = getSession();
  const userInfo = session?.userInfo || {};
  const userName = userInfo.name || session?.user || 'User';
  const userEmail = userInfo.email || session?.email || '';
  const accountType = userInfo.account_type || 'User';

  useEffect(() => {
    Promise.all([getCategories(), getQuestions({ limit: 100 })])
      .then(([loadedCategories, loadedQuestions]) => {
        setCategories(loadedCategories);
        setQuestions(loadedQuestions);
        setExpandedCat(loadedCategories[0]?.id || null);
      })
      .catch((error) => setLoadError(error.message || 'Unable to load chatbot questions.'));
  }, []);

  useEffect(() => {
    const closeProfile = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', closeProfile);
    return () => document.removeEventListener('mousedown', closeProfile);
  }, []);

  useEffect(() => {
    const syncLogout = (event) => {
      if (event.key === 'chemrobotics_session' && !event.newValue) {
        navigate('/login', { replace: true });
      }
    };
    window.addEventListener('storage', syncLogout);
    return () => window.removeEventListener('storage', syncLogout);
  }, [navigate]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const executeSelectedQuestion = async (question, parameters = {}) => {
    if (busy) return;
    setBusy(true);
    setActiveId(question.id);
    setSidebarOpen(false);
    setSelectedQuestion(null);
    const text = (question.questionTemplate || question.displayQuestion || '').replace(/\{(\w+)\}/g, (_, key) => parameters[key] ?? `{${key}}`);

    setMessages((prev) => [
      ...prev,
      { role: 'user', text: text || question.displayQuestion },
      { role: 'bot', loading: true },
    ]);

    try {
      const res = await executeQuestion(question.id, parameters);
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          role: 'bot',
          text: res.count ? `${res.count} result${res.count === 1 ? '' : 's'} found.` : 'No results found.',
          related: question.recommendedQuestions || [],
          result: res.result,
          columns: res.columns || [],
        };
        return next;
      });
    } catch (e) {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          role: 'bot',
          text: e.message || 'Something went wrong while fetching the answer. Please try again.',
        };
        return next;
      });
    } finally {
      setBusy(false);
    }
  };

  const handleSelectQuestion = async (question) => {
    if (busy) return;
    setBusy(true);
    try {
      const detail = await getQuestion(question.id);
      const parameterFields = detail.editableFields?.length
        ? detail.editableFields
        : Object.keys(detail.dbFieldMap || {});
      if (parameterFields.length > 0) {
        setSelectedQuestion({ ...detail, editableFields: parameterFields });
        setBusy(false);
        return;
      }
      setBusy(false);
      await executeSelectedQuestion(detail, detail.defaultParameters || {});
    } catch (error) {
      setMessages((prev) => [...prev, { role: 'bot', text: error.message || 'Unable to load this question.' }]);
      setBusy(false);
    }
  };

  const handleFreeText = async (text) => {
    if (!text || busy) return;
    setBusy(true);
    setMessages((prev) => [...prev, { role: 'user', text }, { role: 'bot', loading: true }]);
    try {
      const res = await askChatbot(text);
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = { role: 'bot', text: res.count ? `${res.count} result${res.count === 1 ? '' : 's'} found.` : 'No results found.', result: res.result, columns: res.columns || [] };
        return next;
      });
    } catch (error) {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = { role: 'bot', text: error.message || 'Unable to answer that question.' };
        return next;
      });
    } finally { setBusy(false); }
  };

  const startNewChat = () => {
    setMessages([]);
    setActiveId(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const questionsByCat = categories.reduce((acc, c) => {
    acc[c.id] = questions.filter((q) => q.categoryId === c.id);
    return acc;
  }, {});

  const hasChat = messages.length > 0;

  return (
    <div className="app">
      <aside className={`sidebar ${sidebarOpen ? 'is-open' : ''}`}>
        <div className="sidebar__brand">
          <img
            className="sidebar__logo-img"
            src={`${process.env.PUBLIC_URL}/assets/pharmvetpat-logo.svg`}
            alt="PharmVetPat by ChemRobotics"
          />
          <div className="sidebar__tag">AI Knowledge Assistant</div>
        </div>

        <button className="sidebar__new" onClick={startNewChat}>
          + New chat
        </button>

        <div className="sidebar__scroll">
          <div className="sidebar__section">Browse by category</div>
          <nav className="sidebar__cats">
          {categories.map((c) => {
            const list = questionsByCat[c.id] || [];
            const open = expandedCat === c.id;
            return (
              <div key={c.id} className="sidebar__group">
                <button
                  className={`sidebar__cat ${open ? 'is-open' : ''}`}
                  onClick={() => setExpandedCat(open ? null : c.id)}
                  aria-expanded={open}
                >
                  <svg
                    className="sidebar__chevron"
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    aria-hidden="true"
                  >
                    <path fill="currentColor" d="M8.5 5l7 7-7 7V5z" />
                  </svg>
                  <span className="sidebar__cat-label">{c.categoryName}</span>
                  <span className="sidebar__cat-count">{list.length}</span>
                </button>

                {open && (
                  <ul className="sidebar__questions">
                    {list.map((q, i) => (
                      <li key={q.id}>
                        <button
                          className={`sidebar__q ${
                            activeId === q.id ? 'is-active' : ''
                          }`}
                          onClick={() => handleSelectQuestion(q)}
                          disabled={busy}
                          title={q.displayQuestion}
                        >
                          <span className="sidebar__q-num">{i + 1}.</span>
                          <span className="sidebar__q-text">{q.displayQuestion}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
          </nav>
        </div>

        <div className="sidebar__footer">
          <a
            href="https://pharmvetpat.chemrobotics.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            pharmvetpat.chemrobotics.com ↗
          </a>
        </div>
      </aside>

      {sidebarOpen && <div className="scrim" onClick={() => setSidebarOpen(false)} />}

      <main className="main">
        <header className="topbar">
          <button
            className="topbar__menu"
            onClick={() => setSidebarOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            <svg viewBox="0 0 24 24" width="22" height="22">
              <path fill="currentColor" d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z" />
            </svg>
          </button>
          <div className="topbar__title">
            ChemRobotics AI Chatbot
            <span className="topbar__badge">Pharma &amp; Agro Intelligence</span>
          </div>
          <div className="chat-profile" ref={profileRef}>
            <button
              type="button"
              className="chat-profile__trigger"
              onClick={() => setProfileOpen((open) => !open)}
              aria-expanded={profileOpen}
              aria-label="Open profile menu"
            >
              <span className="chat-profile__avatar">{userName.charAt(0).toUpperCase()}</span>
              <span className="chat-profile__name">{userName}</span>
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path fill="currentColor" d="M7 10l5 5 5-5H7z" />
              </svg>
            </button>
            {profileOpen && (
              <div className="chat-profile__dropdown">
                <div className="chat-profile__info">
                  <strong>{userName}</strong>
                  {userEmail && <span>{userEmail}</span>}
                  <small>{accountType} account</small>
                </div>
                <button type="button" className="chat-profile__logout" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            )}
          </div>
        </header>

        <div className="chat-scroll" ref={scrollRef}>
          <div className="chat-inner">
            {!hasChat ? (
              <WelcomeScreen onSelectQuestion={handleSelectQuestion} questions={questions} />
            ) : (
              messages.map((m, i) => (
                <Message
                  key={i}
                  role={m.role}
                  text={m.text}
                  source={m.source}
                  loading={m.loading}
                  related={m.related}
                  heading={m.heading}
                  table={m.table}
                  result={m.result}
                  columns={m.columns || []}
                  onSelect={handleSelectQuestion}
                  disabled={busy}
                />
              ))
            )}
          </div>
        </div>

        <div className="composer">
          <div className="composer__inner">
            <ChatInput onSelectQuestion={handleSelectQuestion} onFreeText={handleFreeText} disabled={busy} />
          </div>
        </div>
      </main>

      {loadError && <div className="chat-api-error">{loadError}</div>}
      {selectedQuestion && (
        <QuestionParametersModal
          question={selectedQuestion}
          onSubmit={(parameters) => executeSelectedQuestion(selectedQuestion, parameters)}
          onClose={() => setSelectedQuestion(null)}
        />
      )}
    </div>
  );
}

export default ChatApp;
